// Client HTTP simple pour l'API ACL
class ApiClient {
  constructor(baseURL = null) {
    // URL relative par défaut = proxé par nginx (fonctionne local + ngrok)
    this.baseURL = baseURL || '';
    this.defaultTimeout = 300000; // 300 secondes pour l'IA locale (PC lent)
  }

  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      headers = {},
      body = null,
      timeout = this.defaultTimeout,
      responseType = 'json', // 'json', 'blob', 'text', etc.
    } = options;

    const config = {
      method,
      headers: {
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
    };

    if (body) {
      config.body = body instanceof FormData ? body : JSON.stringify(body);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // 🔒 Gestion 401 : sur un endpoint de login, un 401 signifie "mauvais
      // identifiants" et ne doit surtout pas détruire la session en cours.
      // Ailleurs, c'est un token expiré ou invalide.
      if (response.status === 401) {
        const isLoginEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/admin/login');
        if (!isLoginEndpoint) {
          console.warn('🔒 401 Unauthorized - Token expiré');
          removeAuthToken();
          localStorage.removeItem('auth_client_id');
          localStorage.removeItem('auth_client_nom');
          sessionStorage.clear();
        }
      }

      if (!response.ok) {
        // Remonter le message métier de l'API (detail FastAPI) au lieu de
        // "HTTP 500: Internal Server Error", qui n'aide pas l'utilisateur.
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const payload = await response.json();
          if (payload && payload.detail) {
            if (typeof payload.detail === 'string') {
              message = payload.detail;
            } else if (Array.isArray(payload.detail)) {
              message = payload.detail.map((item) => item.msg).filter(Boolean).join(', ') || message;
            }
          }
        } catch (parseError) {
          // Corps non JSON : on conserve le message HTTP générique
        }
        throw new Error(message);
      }

      // Gestion selon responseType
      let data;
      if (responseType === 'blob') {
        data = await response.blob();
      } else if (responseType === 'text') {
        data = await response.text();
      } else {
        data = await response.json();
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Délai d\'attente dépassé' };
      }
      return { success: false, error: error.message };
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  upload(endpoint, formData, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body: formData });
  }

  put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

// Instance singleton
// Utilise le proxy nginx, donc URL relative (vide)
const api = new ApiClient(process.env.REACT_APP_API_URL || '');

// CORRIGE: Gestion du token JWT
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

export const removeAuthToken = () => {
  localStorage.removeItem('auth_token');
};

// CORRIGE: Surcharge de la méthode request pour injecter le token
const originalRequest = api.request.bind(api);
api.request = async function(endpoint, options = {}) {
  const token = getAuthToken();
  console.log('DEBUG API: Token présent ?', !!token, 'Endpoint:', endpoint);
  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
    console.log('DEBUG API: Headers Authorization:', options.headers['Authorization'] ? 'Oui' : 'Non');
  }
  return originalRequest(endpoint, options);
};

export default api;
